/**
 * Custom listbox built on the WAI-ARIA pattern, used wherever we need a
 * styled dropdown that a native <select> can't host — specifically the
 * two-line option layout (human label over the raw content-type token) and
 * the dark dev-tool palette. Native <select> popups are OS-rendered and
 * ignore our theme, so they show up as light-grey menus on the dark UI.
 *
 * The open panel is rendered through a portal into document.body with
 * fixed positioning anchored to the trigger. This is deliberate: the page's
 * `.fade-up` entrance animation leaves `transform: translateY(0)` applied
 * (animation-fill-mode: forwards), and a non-`none` transform creates a
 * stacking context. Each card section therefore becomes its own stacking
 * layer, so a z-index on the panel can't lift it above a later sibling card
 * — the menu would render *behind* the next card's content. The portal
 * escapes those contexts entirely.
 *
 * Keyboard model mirrors a native select: arrow keys open and move the
 * highlight, Enter/Space commit, Escape/Tab close, Home/End jump.
 */
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

const PANEL_GAP = 8;

export function Select({
  options,
  value,
  onChange,
  ariaLabel,
}: {
  options: ReadonlyArray<{ value: string; label: string }>;
  value: string;
  onChange: (next: string) => void;
  ariaLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [rect, setRect] = useState<{ left: number; top: number; width: number } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const listId = useId();

  const selectedIdx = useMemo(() => {
    const i = options.findIndex((o) => o.value === value);
    return i === -1 ? 0 : i;
  }, [options, value]);
  const selected = options[selectedIdx];

  // Anchor the portaled panel under the trigger. Computed synchronously when
  // opening (so the panel mounts in the right place on first paint) and kept
  // in sync while open as the page scrolls or resizes.
  function measure() {
    const r = triggerRef.current?.getBoundingClientRect();
    if (r) setRect({ left: r.left, top: r.bottom + PANEL_GAP, width: r.width });
  }

  function openMenu() {
    measure();
    setOpen(true);
  }

  useLayoutEffect(() => {
    if (!open) return;
    const update = () => {
      const r = triggerRef.current?.getBoundingClientRect();
      if (r) setRect({ left: r.left, top: r.bottom + PANEL_GAP, width: r.width });
    };
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      // The panel lives in a portal, so it isn't inside wrapRef — check both.
      const insideTrigger = wrapRef.current?.contains(target);
      const insidePanel = listRef.current?.contains(target);
      if (!insideTrigger && !insidePanel) setOpen(false);
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  // Opening starts the highlight on the selected option and moves keyboard
  // focus into the listbox so arrow keys work without a second click.
  useEffect(() => {
    if (open) {
      setHighlight(selectedIdx);
      listRef.current?.focus();
    }
  }, [open, selectedIdx]);

  function commit(idx: number) {
    const o = options[idx];
    if (!o) return;
    onChange(o.value);
    setOpen(false);
    triggerRef.current?.focus();
  }

  function handleTriggerKey(e: React.KeyboardEvent) {
    // Arrow keys open the listbox; Enter/Space fall through to the native
    // button click so we don't fight the browser's keyboard-activation model.
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      openMenu();
    }
  }

  function handleListKey(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(options.length - 1, h + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === "Home") {
      e.preventDefault();
      setHighlight(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setHighlight(options.length - 1);
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      commit(highlight);
    } else if (e.key === "Tab") {
      setOpen(false);
    }
  }

  return (
    <div ref={wrapRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={ariaLabel}
        onClick={() => (open ? setOpen(false) : openMenu())}
        onKeyDown={handleTriggerKey}
        className="flex w-full items-center justify-between gap-3 rounded-md border border-input bg-card px-3 py-2 text-left shadow-xs outline-none transition-[color,box-shadow] hover:bg-muted/50 focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
      >
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] text-foreground">
            {selected?.label ?? ""}
          </span>
          <span className="block truncate font-mono text-[10px] text-muted-foreground/70">
            {selected?.value ?? ""}
          </span>
        </span>
        <span
          aria-hidden
          className={`shrink-0 text-[10px] text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
        >
          ▾
        </span>
      </button>

      {open &&
        rect &&
        createPortal(
          <ul
            ref={listRef}
            id={listId}
            // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: WAI-ARIA listbox pattern, same shape as OrgSwitcher
            // biome-ignore lint/a11y/useSemanticElements: native <select> can't host the two-line option layout
            role="listbox"
            tabIndex={-1}
            aria-label={ariaLabel}
            aria-activedescendant={`${listId}-opt-${highlight}`}
            onKeyDown={handleListKey}
            style={{ position: "fixed", left: rect.left, top: rect.top, width: rect.width }}
            className="z-50 max-h-72 overflow-auto rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-lg focus:outline-none"
          >
            {options.map((o, i) => {
              const isSelected = o.value === value;
              const isHighlighted = i === highlight;
              return (
                <li key={o.value}>
                  <button
                    type="button"
                    id={`${listId}-opt-${i}`}
                    // biome-ignore lint/a11y/useSemanticElements: native <option> can't host a two-line layout; ARIA listbox option is the standard alternative
                    role="option"
                    aria-selected={isSelected}
                    tabIndex={-1}
                    onMouseEnter={() => setHighlight(i)}
                    onClick={() => commit(i)}
                    className={`block w-full rounded-sm px-3 py-2 text-left transition-colors ${
                      isHighlighted ? "bg-muted" : ""
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span
                        className={`text-[13px] ${isSelected ? "text-foreground" : "text-muted-foreground"}`}
                      >
                        {o.label}
                      </span>
                      {isSelected && (
                        <span aria-hidden className="text-[10px] text-foreground">
                          ✓
                        </span>
                      )}
                    </div>
                    <div className="font-mono text-[10px] text-muted-foreground/70">{o.value}</div>
                  </button>
                </li>
              );
            })}
          </ul>,
          document.body,
        )}
    </div>
  );
}
